// Pure presentation helpers for the Dev Cockpit dashboard (DevCockpitView.vue).
// Extracted so the value-mapping / formatting logic can be unit-tested in isolation,
// leaving the view itself as template + API orchestration.
import type {
    CockpitOverview,
    MergeRequestInfo,
    ProjectPipelineInfo,
    SonarProjectInfo,
    SourceStatus,
    VersionCell,
} from "../types/CockpitTypes.ts";
import type { Profile } from "../types/PersologOnlineTypes.ts";

// Version matrix is shown as one tab per environment, prod active by default.
export const ENV_ORDER = ["prod", "staging", "dev"];

const HOUR_MS = 3600 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** GitLab pipeline status → PrimeVue Tag severity. */
export function pipelineSeverity(status: string | null): string {
    switch (status) {
        case "success":
            return "success";
        case "failed":
            return "danger";
        case "running":
        case "pending":
        case "created":
            return "info";
        case "canceled":
        case "skipped":
        case "manual":
            return "warn";
        default:
            return "secondary";
    }
}

/** Jira status category → Tag severity. */
export function statusCategorySeverity(category: string): string {
    switch (category) {
        case "done":
            return "success";
        case "indeterminate":
            return "info";
        default:
            return "secondary"; // "new" / to-do
    }
}

/** Cockpit alert severity → PrimeVue Message severity. */
export function alertSeverity(severity: string): "error" | "warn" | "info" {
    if (severity === "CRITICAL") return "error";
    if (severity === "WARNING") return "warn";
    return "info";
}

/** Per-source fetch status → Tag severity. */
export function sourceSeverity(status: SourceStatus): string {
    if (status === "OK") return "success";
    if (status === "ERROR") return "danger";
    return "secondary";
}

// SonarQube quality-gate status → Tag colour (mirrors pipelineSeverity's shape).
export function gateSeverity(status: string | null): string {
    switch (status) {
        case "OK":
        case "PASSED":
            return "success";
        case "ERROR":
        case "FAILED":
            return "danger";
        case "WARN":
            return "warn";
        default:
            return "secondary"; // NONE / null / unknown
    }
}

/** Profile-generation health state → Tag severity. */
export function healthSeverity(state: string): string {
    if (state === "CRITICAL") return "danger";
    if (state === "WARN") return "warn";
    if (state === "OK") return "success";
    return "secondary";
}

export function formatCoverage(coverage: number | null): string {
    return coverage == null ? "—" : `${coverage}%`;
}

export function formatTime(iso: string | null): string {
    if (!iso) return "—";
    // Backend timestamps are zone-less UTC (same convention as `started` below);
    // only append "Z" when the string carries no offset of its own.
    const normalized = /Z$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + "Z";
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

export function formatAge(seconds: number | null): string {
    if (seconds == null) return "—";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

/** `started` is a zone-less UTC timestamp, matching PendingProfiles.vue's `new Date(started + "Z")`. */
export function formatStarted(started?: string | null): string {
    if (!started) return "—";
    const date = new Date(started + "Z");
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

/** Same >1h (warn) / >1d (critical) heuristic the Support profile-generation page uses. */
export function pendingProfileRowClass(profile: Profile, now: number = Date.now()): string {
    if (!profile.started) return "";
    const age = now - new Date(profile.started + "Z").getTime();
    if (age > DAY_MS) return "bg-primary-300";
    if (age > HOUR_MS) return "bg-primary-100";
    return "";
}

/** A pending participant → the Profile shape the regenerate/recalculate endpoints expect. */
export function toProfile(p: { id: number; questionnaire?: string; selectedProduct: string; project: { language: string } }): Profile {
    return { id: p.id, hash: p.questionnaire ?? "", product: p.selectedProduct, language: p.project.language };
}

/** Alert-worthy conditions in a snapshot: composed alerts + MR pipeline failures + main-pipeline failures. */
export function alertItems(ov: CockpitOverview): Array<{ key: string; title: string; body: string }> {
    const items: Array<{ key: string; title: string; body: string }> = [];
    for (const alert of ov.alerts) {
        items.push({ key: `alert:${alert.source}:${alert.message}`, title: `Cockpit · ${alert.source}`, body: alert.message });
    }
    for (const mr of ov.mergeRequests.data) {
        if (mr.pipelineStatus === "failed") {
            items.push({ key: `mr:${mr.webUrl}`, title: "MR pipeline failed", body: `!${mr.iid} ${mr.project} — ${mr.title}` });
        }
    }
    for (const pipeline of ov.pipelines.data) {
        if (pipeline.status === "failed") {
            const label = pipeline.component ? `${pipeline.project} / ${pipeline.component}` : pipeline.project;
            items.push({
                key: `pipe:${pipeline.project}:${pipeline.component ?? ""}:${pipeline.sha ?? ""}`,
                title: "Main pipeline failed",
                body: label,
            });
        }
    }
    return items;
}

/** Environments that actually have version targets, ordered prod → staging → dev. */
export function versionEnvsInOrder(cells: VersionCell[], order: string[] = ENV_ORDER): string[] {
    const present = new Set(cells.map((cell) => cell.env));
    return order.filter((env) => present.has(env));
}

export function versionsForEnv(cells: VersionCell[], env: string): VersionCell[] {
    return cells.filter((cell) => cell.env === env);
}

// Project paths come from GitLab's path_with_namespace, so the area is the top-level namespace:
// persolog-online (group 23) and internal (26) are areas of their own, and every other namespace
// (devops, infrastructure, container-images, dependencies, tools) counts as infrastructure.
const ONLINE_NAMESPACE = "persolog-online/";
const INTERNAL_NAMESPACE = "internal/";

/** Which area of the GitLab tree a project belongs to; "all" in a filter means no restriction. */
export type ProjectArea = "online" | "internal" | "infra";
export type AreaFilter = ProjectArea | "all";

export interface ProjectFilter {
    text: string;
    area: AreaFilter;
}

export function projectArea(projectPath: string): ProjectArea {
    if (projectPath.startsWith(ONLINE_NAMESPACE)) return "online";
    if (projectPath.startsWith(INTERNAL_NAMESPACE)) return "internal";
    return "infra";
}

/** A URL query value → area filter; anything unknown (or absent) means "all". */
export function parseAreaFilter(value: unknown): AreaFilter {
    return value === "online" || value === "internal" || value === "infra" ? value : "all";
}

export function isFilterActive(filter: ProjectFilter): boolean {
    return filter.text.trim() !== "" || filter.area !== "all";
}

/**
 * True when the project is in the filtered area and the project path or any of the given names contains
 * the text (case-insensitive). A row without a known project only passes an "all" area filter.
 */
function matchesFilter(filter: ProjectFilter, projectPath: string | null | undefined, names: Array<string | null>): boolean {
    if (filter.area !== "all" && (!projectPath || projectArea(projectPath) !== filter.area)) return false;
    const needle = filter.text.trim().toLowerCase();
    return !needle || [projectPath ?? null, ...names].some((name) => name?.toLowerCase().includes(needle));
}

/** MRs match on project path, title, source branch (carries the ticket key) and "!iid". */
export function filterMergeRequests(mrs: MergeRequestInfo[], filter: ProjectFilter): MergeRequestInfo[] {
    return mrs.filter((mr) => matchesFilter(filter, mr.project, [mr.title, mr.sourceBranch, `!${mr.iid}`]));
}

/** Main pipelines match on project path and, for a monorepo row, the app name. */
export function filterPipelines(pipelines: ProjectPipelineInfo[], filter: ProjectFilter): ProjectPipelineInfo[] {
    return pipelines.filter((pipeline) => matchesFilter(filter, pipeline.project, [pipeline.component]));
}

/** Version cells match on the service name and the repo it ships from. */
export function filterVersions(cells: VersionCell[], filter: ProjectFilter): VersionCell[] {
    return cells.filter((cell) => matchesFilter(filter, cell.project, [cell.service]));
}

/** SonarQube rows match on display name, analysis key and the repo they analyse. */
export function filterSonarProjects(projects: SonarProjectInfo[], filter: ProjectFilter): SonarProjectInfo[] {
    return projects.filter((project) => matchesFilter(filter, project.project, [project.name, project.key]));
}
